import React, { useState, Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon, EyeIcon, EyeSlashIcon, UserIcon, EnvelopeIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'
import toast from 'react-hot-toast'

const SettingsModal = ({ isOpen, onClose }) => {
  const { user, setUser } = useAuth()
  const [email, setEmail] = useState(user?.email || '')
  const [name, setName] = useState(user?.name || '')
  const [showName, setShowName] = useState(user ? !!user.showName : true)
  const [saving, setSaving] = useState(false)

  // Follow the user when it loads or changes, without an extra render pass via an effect
  const [prevUser, setPrevUser] = useState(user)
  if (user !== prevUser) {
    setPrevUser(user)
    if (user) {
      setEmail(user.email || '')
      setName(user.name || '')
      setShowName(!!user.showName)
    }
  }

  const handleSave = async () => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      toast.error('Voer een geldig e-mailadres in')
      return
    }

    setSaving(true)
    try {
      const response = await api.put('/users/profile', {
        email: email.trim(),
        name: name.trim(),
        showName
      })
      setUser(response.data.user)

      // Show appropriate message based on response
      if (response.data.message) {
        toast.success(response.data.message)
      } else {
        toast.success('Instellingen succesvol opgeslagen')
      }

      onClose()
    } catch (error) {
      console.error('Fout bij opslaan instellingen:', error)
      const details = error.response?.data?.details
      if (Array.isArray(details) && details.length > 0) {
        toast.error(details.map(detail => detail.message).join('\n'))
      } else {
        toast.error(error.response?.data?.error || 'Er is iets misgegaan')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset form to current user values
    if (user) {
      setEmail(user.email || '')
      setName(user.name || '')
      setShowName(!!user.showName)
    }
    onClose()
  }

  const handleDeleteAccount = () => {
    // Get the unsubscribe token and redirect to unsubscribe page
    if (user?.unsubscribe_token) {
      window.open(`/unsubscribe/${user.unsubscribe_token}`, '_blank')
    } else {
      toast.error('Geen uitschrijf token beschikbaar')
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle
                    as="h3"
                    className="text-xl font-semibold text-gray-900 dark:text-white"
                  >
                    Instellingen
                  </DialogTitle>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="space-y-6">
                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                      <span>E-mailadres</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="je@email.com"
                    />
                    {user?.pendingEmail && (
                      <div className="mt-2 flex items-start space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <ClockIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                            E-mail wijziging in behandeling
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                            Er is een verificatielink gestuurd naar <strong>{user.pendingEmail}</strong>. Klik op de link in de e-mail om de wijziging te voltooien.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Name Setting */}
                  <div>
                    <label
                      htmlFor="name"
                      className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      <UserIcon className="h-4 w-4" />
                      <span>Naam</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Je naam"
                    />
                  </div>

                  {/* Privacy Setting */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                      Privacy
                    </h4>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {showName ? (
                          <EyeIcon className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                        ) : (
                          <EyeSlashIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 shrink-0" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Toon mijn naam bij deelnemers
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {showName
                              ? 'Je naam wordt getoond bij alle meetups'
                              : 'Je verschijnt als "Anoniem" bij alle meetups'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowName(!showName)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                          showName ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            showName ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>Gevarenzone</span>
                  </h4>

                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Account opheffen
                  </button>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Dit opent een nieuwe pagina voor bevestiging
                  </p>
                </div>

                {/* Footer / Actions */}
                <div className="mt-8 flex space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !name.trim() || !email.trim()}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {saving ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default SettingsModal
